/**
 * Enhanced API service with Microsoft Entra ID authentication support.
 */
import axios from 'axios';

// Funkcja do pobierania tokenu z MSAL
let getAccessTokenFunction = null;

// Funkcja do rejestracji funkcji pobierania tokenu
export const registerTokenProvider = (tokenProvider) => {
  getAccessTokenFunction = tokenProvider;
};

// Automatyczne wykrywanie środowiska i URL backendu
const getApiUrl = () => {
  // Jeśli jest zmienna środowiskowa, użyj jej
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Jeśli na Azure (domena zawiera azurewebsites.net lub azurestaticapps.net)
  if (window.location.hostname.includes('azurewebsites.net') || 
      window.location.hostname.includes('azurestaticapps.net')) {
    return 'https://kalendarz-pracy-cxbdhkdjarewegfy.polandcentral-01.azurewebsites.net/api';
  }
     if (window.location.hostname.includes('kalendarz.itvision')) {
    return 'https://kalendarz.itvision.pl/api';
  }
  
  // Development - localhost
  return 'http://127.0.0.1:8000/api';
};

// Create axios instance with base configuration
const api = axios.create({
  baseURL: getApiUrl(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

console.log('🔧 API configured with baseURL:', api.defaults.baseURL);

// Request interceptor for authentication and logging
api.interceptors.request.use(
  async (config) => {
    console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    
    // Dodaj token autoryzacji jeśli dostępny
    if (getAccessTokenFunction) {
      try {
        const token = await getAccessTokenFunction();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          console.log('🔐 Added authorization token to request');
        }
      } catch (error) {
        console.warn('⚠️ Failed to get access token:', error);
        // Kontynuuj bez tokenu - niektóre endpointy mogą być publiczne
      }
    }
    
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  async (error) => {
    console.error('❌ API Response Error:', error);
    
    // Szczegółowe logowanie błędów
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
    } else if (error.request) {
      console.error('Request made but no response:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    
    // Handle authentication errors
    if (error.response?.status === 401) {
      console.warn('🚫 Unauthorized - token may be expired');
      
      // Spróbuj odświeżyć token i ponowić request
      if (getAccessTokenFunction && !error.config._retry) {
        error.config._retry = true;
        
        try {
          const newToken = await getAccessTokenFunction(true); // force refresh
          if (newToken) {
            error.config.headers.Authorization = `Bearer ${newToken}`;
            console.log('🔄 Retrying request with new token');
            return api.request(error.config);
          }
        } catch (tokenError) {
          console.error('❌ Failed to refresh token:', tokenError);
        }
      }
    } else if (error.response?.status === 403) {
      console.error('🚫 Forbidden - insufficient permissions');
    } else if (error.response?.status === 404) {
      console.warn('Resource not found');
    } else if (error.response?.status === 500) {
      console.error('Server error');
    } else if (error.response?.status === 422) {
      console.error('Validation error');
    } else if (error.response?.status === 409) {
      console.error('Conflict error');
    }
    
    return Promise.reject(error);
  }
);

// Authentication API calls
export const authAPI = {
  // Get authentication status
  getStatus: async () => {
    try {
      const response = await api.get('/auth/status');
      return response.data;
    } catch (error) {
      console.error('Failed to get auth status:', error);
      return { configured: false, authenticated: false };
    }
  },
  
  // Get auth configuration
  getConfig: async () => {
    const response = await api.get('/auth/config');
    return response.data;
  },
  
  // Get current user profile
  getProfile: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  }
};

// Employee API calls (unchanged but now with auth support)
export const employeeAPI = {
  // Get all employees
  getAll: async () => {
    console.log('🚀 Starting employeeAPI.getAll()');
    try {
      const response = await api.get('/employees');
      console.log('✅ employeeAPI.getAll() success:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ employeeAPI.getAll() failed:', error);
      throw error;
    }
  },
  
  // Create new employee
  create: async (employeeData) => {
    const response = await api.post('/employees', employeeData);
    return response.data;
  },
  
  // Update employee
  update: async (employeeCode, employeeData) => {
    const response = await api.put(`/employees/${employeeCode}`, employeeData);
    return response.data;
  },
  
  // Delete employee (soft delete)
  delete: async (employeeCode) => {
    await api.delete(`/employees/${employeeCode}`);
  },
  
  // Reorder employees
  reorder: async (employeeCodes) => {
    const response = await api.put('/employees/reorder', {
      employee_codes: employeeCodes
    });
    return response.data;
  }
};

// Work Types API calls
export const workTypeAPI = {
  // Get all work types
  getAll: async () => {
    const response = await api.get('/work-types');
    return response.data;
  },
  
  // Create new work type
  create: async (workTypeData) => {
    const response = await api.post('/work-types', workTypeData);
    return response.data;
  },
  
  // Update work type
  update: async (workTypeCode, workTypeData) => {
    const response = await api.put(`/work-types/${workTypeCode}`, workTypeData);
    return response.data;
  },
  
  // Delete work type
  delete: async (workTypeCode) => {
    await api.delete(`/work-types/${workTypeCode}`);
  }
};

// Schedule API calls
export const scheduleAPI = {
  // Get schedule for month
  getForMonth: async (year, month) => {
    const response = await api.get(`/schedule/${year}/${month}`);
    return response.data;
  },
  
  // Alias for compatibility
  getMonthSchedule: async (year, month) => {
    const response = await api.get(`/schedule/${year}/${month}`);
    return response.data;
  },
  
  // Alias for VacationCalendar compatibility
  getSchedule: async (year, month) => {
    const response = await api.get(`/schedule/${year}/${month}`);
    return response.data;
  },
  
  // Create schedule entry (uses PUT /schedule/cell endpoint)
  createSchedule: async (scheduleData) => {
    const response = await api.put('/schedule/cell', scheduleData);
    return response.data;
  },
  
  // Update schedule entry (uses same PUT /schedule/cell endpoint)
  updateSchedule: async (scheduleData) => {
    const response = await api.put('/schedule/cell', scheduleData);
    return response.data;
  },
  
  // Delete schedule entry
  deleteSchedule: async (employeeCode, year, month, day, updatedBy = 'user') => {
    await api.delete(`/schedule/cell/${employeeCode}/${year}/${month}/${day}?updated_by=${encodeURIComponent(updatedBy)}`);
  },
  
  // Bulk operations
  bulkUpdate: async (scheduleEntries) => {
    const response = await api.post('/schedule/bulk', scheduleEntries);
    return response.data;
  }
};

// Service API calls
export const serviceAPI = {
  // Get services for month
  getForMonth: async (year, month) => {
    const response = await api.get(`/services/${year}/${month}`);
    return response.data;
  },
  
  // Alias for compatibility
  getMonthServices: async (year, month) => {
    const response = await api.get(`/services/${year}/${month}`);
    return response.data;
  },
  
  // Create service
  createService: async (serviceData) => {
    const response = await api.post('/services', serviceData);
    return response.data;
  },
  
  // Update service
  updateService: async (year, month, day, serviceData) => {
    const response = await api.put(`/services/${year}/${month}/${day}`, serviceData);
    return response.data;
  },
  
  // Delete service
  deleteService: async (year, month, day, updatedBy = 'user') => {
    await api.delete(`/services/${year}/${month}/${day}?updated_by=${encodeURIComponent(updatedBy)}`);
  }
};

// Days Off API calls
export const daysOffAPI = {
  // Get all days off (holidays) configurations
  getAll: async (activeOnly = true, skip = 0, limit = 1000) => {
    const response = await api.get(`/days-off?active_only=${activeOnly}&skip=${skip}&limit=${limit}`);
    return response.data;
  },
  
  // Get days off for specific year
  getForYear: async (year) => {
    const response = await api.get(`/days-off/year/${year}`);
    return response.data;
  },
  
  // Get days off for month
  getForMonth: async (year, month) => {
    const response = await api.get(`/days-off/month/${year}/${month}`);
    return response.data;
  },
  
  // Create new day off configuration
  create: async (dayOffData) => {
    const response = await api.post('/days-off', dayOffData);
    return response.data;
  },
  
  // Update day off configuration
  update: async (id, dayOffData) => {
    const response = await api.put(`/days-off/${id}`, dayOffData);
    return response.data;
  },
  
  // Delete day off configuration
  delete: async (id) => {
    await api.delete(`/days-off/${id}`);
  },
  
  // Check if specific date is a day off
  check: async (year, month, day) => {
    const response = await api.post('/days-off/check', { year, month, day });
    return response.data;
  }
};

// Changelog API calls
export const changelogAPI = {
  // Get changelog entries
  getEntries: async (limit = 50, offset = 0) => {
    const response = await api.get(`/changelog?limit=${limit}&offset=${offset}`);
    return response.data;
  },
  
  // Get changes for specific period
  getForPeriod: async (startDate, endDate) => {
    const response = await api.get(`/changelog/period?start_date=${startDate}&end_date=${endDate}`);
    return response.data;
  },

  // Get changelog for specific cell
  getCellChangelog: async (employeeCode, year, month, day, limit = 50) => {
    const response = await api.get(`/changelog/cell/${employeeCode}/${year}/${month}/${day}?limit=${limit}`);
    return response.data;
  },

  // Get changelog for employee in month
  getEmployeeMonthChangelog: async (employeeCode, year, month, limit = 100) => {
    const response = await api.get(`/changelog/employee/${employeeCode}/${year}/${month}?limit=${limit}`);
    return response.data;
  },

  // Get recent changes in system
  getRecentChanges: async (hoursBack = 24, limit = 50, tableName = null, changedBy = null) => {
    const params = new URLSearchParams();
    params.append('hours_back', hoursBack);
    params.append('limit', limit);
    if (tableName) params.append('table_name', tableName);
    if (changedBy) params.append('changed_by', changedBy);
    
    const response = await api.get(`/changelog/recent?${params.toString()}`);
    return response.data;
  },

  // Restore previous value from changelog
  restore: async (changeLogId) => {
    const response = await api.post(`/changelog/restore`, {
      change_log_id: changeLogId,
      confirm_restore: true
    });
    return response.data;
  },

  // Clean up old changelog entries
  cleanup: async (daysToKeep = 30) => {
    const response = await api.post(`/changelog/cleanup?days_to_keep=${daysToKeep}`);
    return response.data;
  }
};

// Vacation Schedule API calls
export const vacationAPI = {
  // Get vacation schedule for year
  getForYear: async (year) => {
    const response = await api.get(`/vacation-schedule/${year}`);
    return response.data;
  },
  
  // Set vacation period
  setVacation: async (vacationData) => {
    const response = await api.post('/vacation-schedule', vacationData);
    return response.data;
  },
  
  // Update vacation
  updateVacation: async (vacationId, vacationData) => {
    const response = await api.put(`/vacation-schedule/${vacationId}`, vacationData);
    return response.data;
  },
  
  // Delete vacation
  deleteVacation: async (vacationId) => {
    await api.delete(`/vacation-schedule/${vacationId}`);
  },

  // Get vacation schedule (monthly view)
  getVacationSchedule: async (year, month, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.work_type_codes) queryParams.append('work_type_codes', params.work_type_codes);
    if (params.employee_codes) queryParams.append('employee_codes', params.employee_codes);
    
    const queryString = queryParams.toString();
    const url = `/vacation-schedule/${year}/${month}${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    return response.data;
  },

  // Get team view (new endpoint)
  getTeamView: async (startDate, endDate, params = {}) => {
    const queryParams = new URLSearchParams();
    queryParams.append('start_date', startDate);
    queryParams.append('end_date', endDate);
    if (params.employee_codes) queryParams.append('employee_codes', params.employee_codes);
    
    const response = await api.get(`/vacation-schedule/team-view?${queryParams.toString()}`);
    return response.data;
  }
};

// Employee Report API calls
export const reportAPI = {
  // Get employee monthly report
  getMonthlyReport: async (employeeCode, year, month) => {
    const response = await api.get(`/employee-report/${employeeCode}/${year}/${month}`);
    return response.data;
  },
  
  // Get team summary
  getTeamSummary: async (year, month) => {
    const response = await api.get(`/employee-report/summary/${year}/${month}`);
    return response.data;
  },
  
  // Export to Excel
  exportToExcel: async (year, month, format = 'xlsx') => {
    const response = await api.get(`/employee-report/export/${year}/${month}`, {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  }
};

// User Preferences API calls
export const userPreferencesAPI = {
  // Get user preferences
  get: async () => {
    const response = await api.get('/user-preferences');
    return response.data;
  },
  
  // Update user preferences
  update: async (preferencesData) => {
    const response = await api.put('/user-preferences', preferencesData);
    return response.data;
  }
};

export default api;